import React, { useEffect, useMemo } from 'react';
import noop from 'lodash/noop';
import { Box, xcss, Stack } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { ErrorMessage } from '@atlaskit/form';
import SectionMessage from '@atlaskit/section-message';
import { RecaptchaTermsAndPolicy, useInvisibleRecaptcha } from '@atlassian/google-recaptcha';
import useCheckboxRecaptcha from '@atlassian/jira-google-recaptcha/src/ui/checkbox-recaptcha/index.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { useFlagsService } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import { useIntl } from '@atlassian/jira-intl';
import messages from './messages.tsx';
import {
	PUBLIC_FORM_RECAPTCHA_SITE_KEY_PROD,
	PUBLIC_FORM_RECAPTCHA_SITE_KEY_STAGING,
	PUBLIC_FORM_ANALYTIC_SOURCE,
	PUBLIC_FORM_RECAPTCHA_ACTION_NAME,
	PUBLIC_FORM_RECAPTCHA_CHECKBOX_SITE_KEY_PROD,
	PUBLIC_FORM_RECAPTCHA_CHECKBOX_SITE_KEY_STAGING,
} from './constants.tsx';

export type PublicFormRecaptchaResult = {
	// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
	getToken: (() => Promise<string | void>) | null;
	loadStatus: 'fail' | 'success' | undefined;
};

type PublicFormRecaptchaProps = {
	onChange: (result: PublicFormRecaptchaResult) => void;
};

type PublicFormCheckboxRecaptchaProps = {
	onCheckboxRecaptchaVerify: (recaptchaToken: string | null) => void;
	onCheckboxRecaptchaExpire: () => void;
	hasVerificationError: boolean; // when submitted but checkbox recaptcha not verified
};

export const PublicFormRecaptcha = ({ onChange }: PublicFormRecaptchaProps) => {
	const environment = useEnvironment();
	const { showFlag } = useFlagsService();
	const { getRecaptchaToken, recaptchaLoadErrorReason, isRecaptchaLoaded } = useInvisibleRecaptcha({
		siteKey:
			environment === 'prod'
				? PUBLIC_FORM_RECAPTCHA_SITE_KEY_PROD
				: PUBLIC_FORM_RECAPTCHA_SITE_KEY_STAGING,
		displayBadge: false,
		analyticsChannel: 'jira',
		analyticsSource: PUBLIC_FORM_ANALYTIC_SOURCE,
	});

	useEffect(() => {
		// the google recaptcha package fails to load
		if (!isRecaptchaLoaded && recaptchaLoadErrorReason) {
			showFlag({
				messageId:
					'business-form.ui.form-submission.fill-in-view.public-form-recaptcha.show-flag.error',
				messageType: 'transactional',
				type: 'error',
				title: messages.recaptchaErrorFlagTitle,
				description: messages.recaptchaErrorFlagContent,
				isAutoDismiss: true,
			});

			onChange({
				loadStatus: 'fail',
				getToken: null,
			});
		}

		// the google recaptcha package loads successfully
		if (isRecaptchaLoaded && !recaptchaLoadErrorReason) {
			onChange({
				loadStatus: 'success',
				getToken: () =>
					getRecaptchaToken(PUBLIC_FORM_RECAPTCHA_ACTION_NAME).catch(() => {
						// recaptcha token fails to generate
						showFlag({
							messageId:
								'business-form.ui.form-submission.fill-in-view.public-form-recaptcha.show-flag.error.1',
							messageType: 'transactional',
							type: 'error',
							title: messages.recaptchaErrorFlagTitle,
							description: messages.recaptchaErrorFlagContent,
							isAutoDismiss: true,
						});
					}),
			});
		}
	}, [onChange, isRecaptchaLoaded, getRecaptchaToken, recaptchaLoadErrorReason, showFlag]);

	return (
		<Box
			xcss={recaptchaFooterStyles}
			testId="business-form.ui.form-submission.fill-in-view.public-form-recaptcha.public-form-recaptcha"
		>
			<RecaptchaTermsAndPolicy />
		</Box>
	);
};

export const PublicFormCheckboxRecaptcha = ({
	onCheckboxRecaptchaVerify,
	onCheckboxRecaptchaExpire,
	hasVerificationError,
}: PublicFormCheckboxRecaptchaProps) => {
	const { showFlag } = useFlagsService();
	const environment = useEnvironment();
	const { formatMessage } = useIntl();

	const callbacks = useMemo(
		() => ({
			onVerify: onCheckboxRecaptchaVerify,
			onLoad: noop,
			onError: () =>
				showFlag({
					messageId:
						'business-form.ui.form-submission.fill-in-view.public-form-recaptcha.show-flag.error.2',
					messageType: 'transactional',
					type: 'error',
					title: messages.recaptchaCheckboxErrorFlagTitle,
					description: messages.recaptchaCheckboxErrorFlagContent,
					isAutoDismiss: true,
				}),
			onExpire: onCheckboxRecaptchaExpire,
			onRender: noop,
			onScriptError: () =>
				showFlag({
					messageId:
						'business-form.ui.form-submission.fill-in-view.public-form-recaptcha.show-flag.error.3',
					messageType: 'transactional',
					type: 'error',
					title: messages.recaptchaCheckboxErrorFlagTitle,
					description: messages.recaptchaCheckboxErrorFlagContent,
					isAutoDismiss: true,
				}),
		}),
		[onCheckboxRecaptchaVerify, onCheckboxRecaptchaExpire, showFlag],
	);
	const { recaptchaComponent } = useCheckboxRecaptcha(
		{
			sitekey:
				environment === 'prod'
					? PUBLIC_FORM_RECAPTCHA_CHECKBOX_SITE_KEY_PROD
					: PUBLIC_FORM_RECAPTCHA_CHECKBOX_SITE_KEY_STAGING,
		},
		callbacks,
	);

	return (
		<Stack space="space.100">
			<SectionMessage appearance="warning">
				{formatMessage(messages.checkboxRecaptchaSectionMessage)}
			</SectionMessage>
			<Box
				xcss={[recaptchaCheckboxStyle, hasVerificationError && recaptchaCheckboxErrorStyle]}
				testId="business-form.ui.form-submission.fill-in-view.public-form-recaptcha.public-form-checkbox-recaptcha"
			>
				{recaptchaComponent}
			</Box>
			{hasVerificationError && (
				<ErrorMessage>
					{formatMessage(messages.checkboxRecaptchaHasVerificationErrorMessage)}
				</ErrorMessage>
			)}
		</Stack>
	);
};

const recaptchaCheckboxStyle = xcss({
	display: 'flex',
	alignSelf: 'flex-start',
});

const recaptchaCheckboxErrorStyle = xcss({
	borderColor: 'color.border.danger',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderRadius: 'border.radius.100',
});

const recaptchaFooterStyles = xcss({
	color: 'color.text.subtlest',
	font: token('font.body.small'),
});
